import { createFileRoute } from '@tanstack/react-router'
import { useCallback, useEffect, useMemo, useState } from "react"
import { useAuth } from "@/lib/auth-context"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Plus, ShoppingCart, Trash2, Search } from "lucide-react"
import { formatBRDate, todayISODate } from "@/lib/br-dates"
import { formatBRL as formatMoney, maskMoneyInput, splitInstallments, toMoneyNumber } from "@/lib/money"
import { COMPRAS_PARCELAS_CONFIG } from "@/features/pagamentos/parcelas-logic"
import { useParcelas } from "@/features/pagamentos/use-parcelas"
import { API_BASE } from '@/lib/api-base'

interface ItemEstoque {
  id: string
  codigo: string | null
  nome: string
  categoria: string
  unidade: string
  unidade_compra: string | null
  fator_conversao: number
}

interface Estoque {
  id: string
  nome: string
  status: string
}

interface Fornecedor {
  id: string
  nome: string
  documento: string
  telefone: string
  cidade: string
}

interface ItemCompra {
  item_id: string
  item_nome: string
  unidade: string
  unidade_compra: string | null
  fator_conversao: number
  quantidade_compra: number
  preco_unitario: number
  subtotal: number
}

function ComprasPage() {
  const apiBase = API_BASE
  const { user, apiFetch, isSuperAdmin, selectedTenantId } = useAuth()

  const [itens, setItens] = useState<ItemEstoque[]>([])
  const [estoques, setEstoques] = useState<Estoque[]>([])
  const [fornecedores, setFornecedores] = useState<Fornecedor[]>([])
  
  const [itensCompra, setItensCompra] = useState<ItemCompra[]>([])
  const [showAddItem, setShowAddItem] = useState(false)
  const [searchItem, setSearchItem] = useState("")
  
  const [fornecedorId, setFornecedorId] = useState("")
  const [documentoRef, setDocumentoRef] = useState("")
  const [dataCompra, setDataCompra] = useState(todayISODate())
  const [observacao, setObservacao] = useState("")
  
  const [gerarConta, setGerarConta] = useState(false)
  const [valorEntrada, setValorEntrada] = useState("")
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const [novoItem, setNovoItem] = useState({
    item_id: "",
    quantidade: 0,
    preco_unitario: ""
  })

  const fetchData = useCallback(async () => {
    try {
      const [itensRes, estoquesRes, fornRes] = await Promise.all([
        apiFetch(`${apiBase}/api/estoque/itens`),
        apiFetch(`${apiBase}/api/estoque/locais`),
        apiFetch(`${apiBase}/api/fornecedores/`)
      ])
      if (itensRes.ok) setItens(await itensRes.json())
      if (estoquesRes.ok) setEstoques((await estoquesRes.json()).filter((e: Estoque) => e.status === "ATIVO"))
      if (fornRes.ok) setFornecedores(await fornRes.json())
    } catch (e) { console.error(e) }
  }, [apiFetch, apiBase])

  useEffect(() => {
    if (!user) return
    if (isSuperAdmin && !selectedTenantId) return
    void fetchData()
  }, [user, isSuperAdmin, selectedTenantId, fetchData])

  const valorTotal = itensCompra.reduce((sum, i) => sum + i.subtotal, 0)
  const estoquePrincipal = estoques[0]?.id || ""

  const formatBRL = (v: number) => formatMoney(v)
  
  const parseBRL = (s: string | number): number => {
    if (typeof s === "string" && s.toLowerCase() === "restante") return 0
    return toMoneyNumber(s)
  }

  const valorEntradaNum = parseBRL(valorEntrada)

  const {
    parcelas: parcelasPersonalizadas,
    setParcelas: setParcelasPersonalizadas,
    novaParcela,
    setNovaParcela,
    adicionarParcela,
    removerParcela,
    adicionarParcelaRapidaPorTipo,
    calcularDataParcela,
    saldoDevedor,
    totalParcelas,
    restanteParaPagar,
  } = useParcelas({
    ...COMPRAS_PARCELAS_CONFIG,
    getSaldoDevedor: () => valorTotal - valorEntradaNum,
    getDataBaseFallback: () => dataCompra,
  })

  const totalProgramado = valorEntradaNum + totalParcelas
  const diferenca = valorTotal - totalProgramado

  const itensFiltrados = useMemo(
    () =>
      itens.filter(
        i =>
          i.nome.toLowerCase().includes(searchItem.toLowerCase()) ||
          i.codigo?.toLowerCase().includes(searchItem.toLowerCase()),
      ),
    [itens, searchItem],
  )

  const handleAddItem = () => {
    const item = itens.find(i => i.id === novoItem.item_id)
    const precoNum = parseBRL(novoItem.preco_unitario)
    if (!item || novoItem.quantidade <= 0 || precoNum <= 0) return

    setItensCompra([...itensCompra, {
      item_id: item.id,
      item_nome: item.nome,
      unidade: item.unidade,
      unidade_compra: item.unidade_compra,
      fator_conversao: item.fator_conversao,
      quantidade_compra: novoItem.quantidade,
      preco_unitario: precoNum,
      subtotal: novoItem.quantidade * precoNum
    }])

    setNovoItem({ item_id: "", quantidade: 0, preco_unitario: "" })
    setShowAddItem(false)
    setSearchItem("")
  }

  const removeItem = (idx: number) => {
    setItensCompra(itensCompra.filter((_, i) => i !== idx))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitError(null)
    setSubmitSuccess(null)

    if (itensCompra.length === 0) {
      setSubmitError("Adicione pelo menos um item")
      return
    }

    if (!estoquePrincipal) {
      setSubmitError("Nenhum local de estoque cadastrado")
      return
    }

    setLoading(true)

    const falhasItens: string[] = []
    const falhasContas: string[] = []

    try {
      for (const item of itensCompra) {
        const qtdUso = item.unidade_compra ? item.quantidade_compra * item.fator_conversao : item.quantidade_compra

        const res = await apiFetch(`${apiBase}/api/estoque/movimentacoes`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            estoque_id: estoquePrincipal,
            item_id: item.item_id,
            tipo: "ENTRADA",
            quantidade: qtdUso,
            preco_unitario: item.preco_unitario / (item.fator_conversao || 1),
            data_movimentacao: dataCompra,
            documento_ref: documentoRef || null,
            observacao: observacao || `Compra - ${item.quantidade_compra} ${item.unidade_compra || item.unidade} x ${formatBRL(item.preco_unitario)}`
          })
        })
        if (!res.ok) {
          const errData = await res.json().catch(() => ({})) as { detail?: string }
          falhasItens.push(`${item.item_nome}: ${errData?.detail || "erro ao registrar"}`)
        }
      }

      if (gerarConta && fornecedorId && valorTotal > 0) {
        if (valorEntradaNum > 0) {
          const res = await apiFetch(`${apiBase}/api/contas-pagar/`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              tipo_conta: "FORNECEDOR",
              fornecedor_id: fornecedorId,
              descricao: `Compra - Entrada - ${documentoRef || dataCompra}`,
              valor: valorEntradaNum,
              data_vencimento: dataCompra,
              categoria: "insumos",
              documento_ref: documentoRef || null,
              observacao: "Entrada paga no ato da compra"
            })
          })
          if (!res.ok) {
            const errData = await res.json().catch(() => ({})) as { detail?: string }
            falhasContas.push(`Entrada: ${errData?.detail || "erro ao registrar"}`)
          }
        }

        for (let i = 0; i < parcelasPersonalizadas.length; i++) {
          const p = parcelasPersonalizadas[i]
          const valorParcela = parseBRL(p.valor)
          if (valorParcela > 0) {
            const res = await apiFetch(`${apiBase}/api/contas-pagar/`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                tipo_conta: "FORNECEDOR",
                fornecedor_id: fornecedorId,
                descricao: `Compra - Parcela ${i + 1}/${parcelasPersonalizadas.length} - ${documentoRef || dataCompra}`,
                valor: valorParcela,
                data_vencimento: calcularDataParcela(p.prazo, p.tipoPrazo, p.dataBase),
                categoria: "insumos",
                documento_ref: documentoRef || null,
                observacao: observacao || null
              })
            })
            if (!res.ok) {
              const errData = await res.json().catch(() => ({})) as { detail?: string }
              falhasContas.push(`Parcela ${i + 1}/${parcelasPersonalizadas.length}: ${errData?.detail || "erro ao registrar"}`)
            }
          }
        }
      }

      if (falhasItens.length === 0 && falhasContas.length === 0) {
        const contasCriadas = gerarConta && fornecedorId ?
          (valorEntradaNum > 0 ? 1 : 0) + parcelasPersonalizadas.length : 0

        setSubmitSuccess(`Compra registrada! ${itensCompra.length} item(ns) abastecidos.${contasCriadas > 0 ? ` ${contasCriadas} conta(s) a pagar criada(s).` : ''}`)
        setItensCompra([])
        setFornecedorId("")
        setDocumentoRef("")
        setObservacao("")
        setGerarConta(false)
        setValorEntrada("")
        setParcelasPersonalizadas([])
      } else {
        const itensOk = itensCompra.length - falhasItens.length
        const partes: string[] = []
        if (falhasItens.length > 0) {
          partes.push(`${itensOk} de ${itensCompra.length} itens registrados no estoque. Itens com erro: ${falhasItens.join('; ')}`)
        } else {
          partes.push(`Todos os ${itensCompra.length} itens foram registrados no estoque`)
        }
        if (falhasContas.length > 0) {
          partes.push(`Falha ao gerar conta(s) a pagar: ${falhasContas.join('; ')}`)
        }
        setSubmitError(partes.join('. ') + '.')
      }

    } catch (e) {
      console.error(e)
      setSubmitError("Erro ao registrar compra")
    } finally {
      setLoading(false)
    }
  }

  if (isSuperAdmin && !selectedTenantId) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Selecione uma granja no menu lateral.</p>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6 max-w-4xl mx-auto">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ShoppingCart className="h-6 w-6" /> Compras
          </h1>
          <p className="text-muted-foreground text-sm">
            Registre compras e abasteça o estoque automaticamente
          </p>
        </div>
      </div>

      {submitSuccess && (
        <Card className="border-green-200 bg-green-50 dark:bg-green-950/20">
          <CardContent className="pt-4">
            <p className="text-green-800 dark:text-green-200">{submitSuccess}</p>
          </CardContent>
        </Card>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Dados da Compra</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <Label>Fornecedor</Label>
                <select
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2"
                  value={fornecedorId}
                  onChange={e => setFornecedorId(e.target.value)}
                >
                  <option value="">Nao informar</option>
                  {fornecedores.map(f => (
                    <option key={f.id} value={f.id}>
                      {f.nome} {f.documento ? `- ${f.documento}` : ''} {f.cidade ? `(${f.cidade})` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Documento / NF</Label>
                <Input
                  placeholder="Ex: NF 12345"
                  value={documentoRef}
                  onChange={e => setDocumentoRef(e.target.value)}
                />
              </div>
              <div>
                <Label>Data</Label>
                <Input
                  type="date"
                  value={dataCompra}
                  onChange={e => setDataCompra(e.target.value)}
                />
              </div>
            </div>
            <div>
              <Label>Observação</Label>
              <Input
                placeholder="Opcional"
                value={observacao}
                onChange={e => setObservacao(e.target.value)}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle className="text-lg">Itens</CardTitle>
              <Button type="button" variant="outline" size="sm" onClick={() => setShowAddItem(!showAddItem)}>
                <Plus className="h-4 w-4 mr-1" /> Adicionar
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {showAddItem && (
              <Card className="bg-muted/50">
                <CardContent className="pt-4 space-y-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Buscar item..."
                      className="pl-9"
                      value={searchItem}
                      onChange={e => setSearchItem(e.target.value)}
                    />
                  </div>
                  
                  <div className="grid gap-2 max-h-48 overflow-y-auto">
                    {itensFiltrados.slice(0, 10).map(i => (
                      <div
                        key={i.id}
                        className={`p-2 rounded border cursor-pointer hover:bg-accent ${novoItem.item_id === i.id ? "border-primary bg-primary/10" : ""}`}
                        onClick={() => setNovoItem({ ...novoItem, item_id: i.id })}
                      >
                        <p className="font-medium">{i.nome}</p>
                        <p className="text-xs text-muted-foreground">
                          {i.codigo && <span className="font-mono">{i.codigo} | </span>}
                          Uso: {i.unidade}
                          {i.unidade_compra && <span> | Compra: {i.unidade_compra} (1 = {i.fator_conversao} {i.unidade})</span>}
                        </p>
                      </div>
                    ))}
                  </div>

                  {novoItem.item_id && (
                    <div className="grid grid-cols-2 gap-3 pt-2 border-t">
                      <div>
                        <Label>Quantidade ({itens.find(i => i.id === novoItem.item_id)?.unidade_compra || itens.find(i => i.id === novoItem.item_id)?.unidade})</Label>
                        <Input
                          type="number"
                          step="0.001"
                          min="0.001"
                          value={novoItem.quantidade || ""}
                          onChange={e => setNovoItem({ ...novoItem, quantidade: parseFloat(e.target.value) || 0 })}
                        />
                      </div>
                      <div>
                        <Label>Preco Unitario</Label>
                        <Input
                          placeholder="R$ 0,00"
                          value={novoItem.preco_unitario}
                          onChange={e => setNovoItem({ ...novoItem, preco_unitario: maskMoneyInput(e.target.value) })}
                        />
                      </div>
                      <div className="col-span-2 flex justify-between items-center">
                        <p className="text-sm">
                          <strong>Subtotal:</strong> {formatBRL(novoItem.quantidade * parseBRL(novoItem.preco_unitario))}
                        </p>
                        <Button type="button" size="sm" onClick={handleAddItem}>Adicionar</Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {itensCompra.length === 0 ? (
              <div className="text-sm text-muted-foreground">
                <p>Adicione os itens para registrar a compra e atualizar o estoque.</p>
                <Button type="button" variant="outline" size="sm" className="mt-3" onClick={() => setShowAddItem(true)}><Plus className="mr-1 h-4 w-4" />Adicionar item</Button>
              </div>
            ) : (
              <div className="space-y-2">
                {itensCompra.map((item, idx) => (
                  <div key={idx} className="flex justify-between items-center p-3 bg-muted/30 rounded-lg">
                    <div>
                      <p className="font-medium">{item.item_nome}</p>
                      <p className="text-sm text-muted-foreground">
                        {item.quantidade_compra} {item.unidade_compra || item.unidade} x {formatBRL(item.preco_unitario)}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <p className="font-medium">{formatBRL(item.subtotal)}</p>
                      <Button type="button" variant="ghost" size="sm" aria-label={`Remover ${item.item_nome}`} onClick={() => removeItem(idx)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {itensCompra.length > 0 && (
              <div className="flex justify-end pt-3 border-t">
                <p className="text-lg font-bold">Total: {formatBRL(valorTotal)}</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle className="text-lg">Pagamento</CardTitle>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={gerarConta}
                  onChange={e => setGerarConta(e.target.checked)}
                  className="rounded"
                />
                Gerar conta(s) a pagar
              </label>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {!gerarConta ? (
              <p className="text-muted-foreground text-sm">
                Marque para registrar a(s) conta(s) a pagar no financeiro.
              </p>
            ) : (
              <div className="space-y-4">
                {!fornecedorId && (
                  <div className="p-3 bg-amber-50 dark:bg-amber-950/20 rounded-lg text-sm text-amber-800 dark:text-amber-200">
                    Selecione um fornecedor para gerar contas a pagar
                  </div>
                )}
                
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-sm font-medium mb-2">Total da compra: {formatBRL(valorTotal)}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Entrada (pago hoje)</Label>
                    <Input
                      placeholder="R$ 0,00"
                      value={valorEntrada}
                      onChange={e => setValorEntrada(maskMoneyInput(e.target.value))}
                    />
                  </div>
                  <div>
                    <Label>Saldo a pagar</Label>
                    <Input
                      value={formatBRL(saldoDevedor)}
                      disabled
                      className="bg-muted"
                    />
                  </div>
                </div>

                {saldoDevedor > 0 && (
                  <>
                    <div className="p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg mb-4">
                      <p className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">
                        Como funciona:
                      </p>
                      <ul className="text-xs text-blue-700 dark:text-blue-300 space-y-1">
                        <li><strong>Restante:</strong> Calcula automaticamente o valor que falta pagar</li>
                        <li><strong>Parcelar em:</strong> Em quantas vezes quer dividir (ex: 4 = 4 parcelas)</li>
                        <li><strong>Intervalo:</strong> Tempo entre cada parcela (ex: 1 mes = uma por mes)</li>
                        <li><strong>Tipo:</strong> Dias, semanas ou meses</li>
                      </ul>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-4">
                      <div className="flex items-center gap-2 pt-6">
                        <input
                          type="checkbox"
                          checked={novaParcela.ehRestante}
                          onChange={e => setNovaParcela({ ...novaParcela, ehRestante: e.target.checked })}
                          className="rounded"
                        />
                        <Label className="cursor-pointer">Restante</Label>
                      </div>
                      <div>
                        <Label>Valor</Label>
                        <Input
                          placeholder="R$ 0,00"
                          value={novaParcela.ehRestante ? formatBRL(restanteParaPagar) : novaParcela.valor}
                          onChange={e => setNovaParcela({ ...novaParcela, valor: maskMoneyInput(e.target.value) })}
                          disabled={novaParcela.ehRestante}
                        />
                      </div>
                      <div>
                        <Label>Parcelar em</Label>
                        <Input
                          type="number"
                          min="1"
                          max="48"
                          value={novaParcela.parcelarEm || ""}
                          onChange={e => setNovaParcela({ ...novaParcela, parcelarEm: parseInt(e.target.value) || 1 })}
                        />
                      </div>
                      <div>
                        <Label>Intervalo</Label>
                        <Input
                          type="number"
                          min="1"
                          value={novaParcela.prazo || ""}
                          onChange={e => setNovaParcela({ ...novaParcela, prazo: parseInt(e.target.value) || 0 })}
                        />
                      </div>
                      <div>
                        <Label>Tipo</Label>
                        <select
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2"
                          value={novaParcela.tipoPrazo}
                          onChange={e => setNovaParcela({ ...novaParcela, tipoPrazo: e.target.value as "dias" | "semanas" | "meses" })}
                        >
                          <option value="dias">Dias</option>
                          <option value="semanas">Semanas</option>
                          <option value="meses">Meses</option>
                        </select>
                      </div>
                      <div className="flex items-end">
                        <Button type="button" variant="outline" onClick={adicionarParcela} className="w-full">
                          <Plus className="h-4 w-4 mr-1" /> Add
                        </Button>
                      </div>
                    </div>

                    {novaParcela.ehRestante && novaParcela.parcelarEm > 0 && novaParcela.prazo > 0 && (
                      <div className="p-3 bg-green-50 dark:bg-green-950/20 rounded-lg mb-4 text-sm">
                        <p className="text-green-800 dark:text-green-200">
                          {(() => {
                            const valores = splitInstallments(restanteParaPagar, novaParcela.parcelarEm)
                            const n = novaParcela.parcelarEm
                            const unidade = novaParcela.tipoPrazo
                            const intervalo = novaParcela.prazo
                            if (n === 1) {
                              return <><strong>Sera criada 1 parcela de {formatBRL(valores[0])}</strong><br />com intervalo de {intervalo} {unidade}</>
                            }
                            const ultima = valores[valores.length - 1]
                            const iguais = valores.every(v => v === valores[0])
                            return iguais ? (
                              <><strong>Serao criadas {n} parcelas de {formatBRL(valores[0])}</strong><br />com intervalo de {intervalo} {unidade}</>
                            ) : (
                              <><strong>Serao criadas {n} parcelas de {formatBRL(valores[0])} (ultima {formatBRL(ultima)})</strong><br />com intervalo de {intervalo} {unidade}. O centavo restante vai para a ultima parcela.</>
                            )
                          })()}
                        </p>
                      </div>
                    )}

                    <div className="flex flex-wrap gap-2 mb-4">
                      <Button type="button" variant="ghost" size="sm" onClick={() => adicionarParcelaRapidaPorTipo(1, 30, "dias")}>
                        1x em 30 dias
                      </Button>
                      <Button type="button" variant="ghost" size="sm" onClick={() => adicionarParcelaRapidaPorTipo(2, 1, "meses")}>
                        2x mensal
                      </Button>
                      <Button type="button" variant="ghost" size="sm" onClick={() => adicionarParcelaRapidaPorTipo(3, 1, "meses")}>
                        3x mensal
                      </Button>
                      <Button type="button" variant="ghost" size="sm" onClick={() => adicionarParcelaRapidaPorTipo(4, 1, "meses")}>
                        4x mensal
                      </Button>
                      <Button type="button" variant="ghost" size="sm" onClick={() => adicionarParcelaRapidaPorTipo(12, 1, "meses")}>
                        12x mensal
                      </Button>
                      <Button type="button" variant="ghost" size="sm" onClick={() => adicionarParcelaRapidaPorTipo(4, 1, "semanas")}>
                        4x semanal
                      </Button>
                    </div>

                    {parcelasPersonalizadas.length > 0 && (
                      <div className="space-y-2">
                        {parcelasPersonalizadas.map((p, i) => (
                          <div key={i} className="flex justify-between items-center p-3 bg-muted/50 rounded-lg">
                            <div>
                              <span className="font-medium">Parcela {i + 1}</span>
                              <span className="text-muted-foreground ml-2">
                                em {p.prazo} {p.tipoPrazo} 
                                {p.dataBase && <span className="text-xs">(a partir de {formatBRDate(p.dataBase)})</span>}
                              </span>
                              <span className="text-xs text-muted-foreground ml-2">
                                = {formatBRDate(calcularDataParcela(p.prazo, p.tipoPrazo, p.dataBase))}
                              </span>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="font-medium">{formatBRL(parseBRL(p.valor))}</span>
                              <Button type="button" variant="ghost" size="sm" onClick={() => removerParcela(i)}>
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {parcelasPersonalizadas.length > 0 && (
                      <div className={`mt-4 p-3 rounded-lg ${Math.abs(diferenca) < 0.01 ? 'bg-green-50 dark:bg-green-950/20' : 'bg-amber-50 dark:bg-amber-950/20'}`}>
                        {Math.abs(diferenca) < 0.01 ? (
                          <p className="text-green-800 dark:text-green-200 text-sm">
                            Valor total corresponde as parcelas
                          </p>
                        ) : (
                          <p className="text-amber-800 dark:text-amber-200 text-sm">
                            Diferenca: {diferenca > 0 ? 'Faltam' : 'Sobram'} {formatBRL(Math.abs(diferenca))}
                          </p>
                        )}
                      </div>
                    )}
                  </>
                )}

                {saldoDevedor === 0 && valorEntradaNum > 0 && (
                  <div className="bg-green-50 dark:bg-green-950/20 rounded-lg p-4">
                    <p className="text-green-800 dark:text-green-200">
                      Pagamento a vista: {formatBRL(valorEntradaNum)}
                    </p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {submitError && (
          <div className="p-3 bg-destructive/10 text-destructive rounded-md text-sm">
            {submitError}
          </div>
        )}

        <div className="flex gap-2 justify-end">
          <Button type="button" variant="outline" onClick={() => {
            setItensCompra([])
            setFornecedorId("")
            setDocumentoRef("")
            setObservacao("")
            setGerarConta(false)
            setValorEntrada("")
            setParcelasPersonalizadas([])
          }}>
            Limpar
          </Button>
          <Button type="submit" disabled={loading || itensCompra.length === 0}>
            {loading ? "Salvando..." : "Registrar Compra"}
          </Button>
        </div>
      </form>
    </div>
  )
}

export const Route = createFileRoute('/_authenticated/compras')({
  component: ComprasPage,
})
